import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

/**
 * The mock's data, generated from the local database by
 * workbench/backlog/feature_watchdog/issue-deepdive-page/notes/gen-deepdive-fixture.mjs into public/,
 * where it is gitignored: one folder per workspace, with the site's figures and its monitored URIs in
 * index.json and one file per page. Absolute, because package.json sets "homepage": "." and a relative
 * URL would resolve against the route.
 */
const folderOf = (workspaceNumber) => `/watchdog/${workspaceNumber}/deepdive`;

const fetchJson = (url) => fetch(url).then((response) => {
    if (!response.ok) throw new Error(`${response.status} fetching ${url}`);
    // A build without the file answers every path with index.html and a 200.
    if (!(response.headers.get("content-type") || "").includes("json")) throw new Error(`${url} is not there`);
    return response.json();
});

const fetched = new Map();

/** The file at `path` in the workspace's folder, fetched once. */
const fetchFile = (workspaceNumber, path) => {
    const url = `${folderOf(workspaceNumber)}/${path}`;
    if (!fetched.has(url)) fetched.set(url, fetchJson(url).catch((error) => { fetched.delete(url); throw error; }));
    return fetched.get(url);
};

/** The site's figures and the page `id`, once both are in. */
export const useDeepDiveFixture = (id) => {
    const { workspaceNumber } = useParams();
    const [state, setState] = useState({ site: null, page: null, error: null });

    useEffect(() => {
        let live = true;
        setState({ site: null, page: null, error: null });
        Promise.all([fetchFile(workspaceNumber, "index.json"), fetchFile(workspaceNumber, `${id}.json`)]).then(
            ([site, page]) => live && setState({ site, page, error: null }),
            (error) => live && setState({ site: null, page: null, error: error.message }),
        );
        return () => { live = false; };
    }, [workspaceNumber, id]);

    return state;
};

/**
 * The files of other pages, by id: the ones put beside this one, or the ones suggested; an id not in
 * the map is still loading. A page in the index without a file has no issue and no Google data in the
 * window: it is there, as null.
 */
export const usePageFiles = (ids) => {
    const { workspaceNumber } = useParams();
    const key = ids.join(",");
    const [byId, setById] = useState(new Map());

    useEffect(() => {
        let live = true;
        const wanted = key ? key.split(",") : [];
        Promise.all(wanted.map((id) => fetchFile(workspaceNumber, `${id}.json`).then((page) => [id, page], () => [id, null])))
            .then((entries) => live && setById(new Map(entries)));
        return () => { live = false; };
    }, [workspaceNumber, key]);

    return byId;
};
